import { useCurrencyFormatter } from "@app/hooks/use-formatter";
import { useMemo } from "react";
import type { HistogramItem } from "../shared-histogram";
import { SharedHistogram } from "../shared-histogram";

interface TransactionHistogramProps {
  stakeholderName: string;
  // Matches the Relay fragment shape passed by StakeholderRow: readonly arrays,
  // and amount/bankAccount/last4 are nullable in the schema.
  data: {
    txns: ReadonlyArray<{
      id: string;
      description: string;
      postingDate: string;
      amount: number | null | undefined;
      bankAccount:
        | {
            nickname: string;
            last4: string | null | undefined;
            bank: {
              iconSvg: string;
              name: string;
            };
          }
        | null
        | undefined;
    }>;
  };
  ratio?: number;
}

export default function TransactionHistogram({
  data,
  stakeholderName,
  ratio,
}: TransactionHistogramProps) {
  const currencyFormatter = useCurrencyFormatter();

  const items: HistogramItem[] = useMemo(
    () =>
      data.txns.map((t) => {
        const formattedAmount = currencyFormatter.format(-(t.amount ?? 0) / 100);
        const label = (
          <div>
            <div className="mb-1">{`We sent to ${stakeholderName}:`}</div>
            <div className="relative">
              <div className="border border-default px-2 py-1 text-[14px] flex font-mono gap-3 justify-between">
                <div>{t.postingDate}</div>
                <div>{t.description}</div>
                <div>{formattedAmount}</div>
              </div>
              <div className="opacity-70 uppercase text-[12px] text-right pl-3 pr-2 pt-1 right-0 absolute flex items-center justify-end gap-1">
                {/* 1. Handle the Nickname prefix */}
                {t.bankAccount?.nickname && (
                  <span>{t.bankAccount.nickname} (</span>
                )}

                {/* 2. Render the SVG using dangerouslySetInnerHTML */}
                <span
                  className="w-16 h-5 pt-[3px] inline-block fill-current" // Adjust width/height classes as needed
                  // eslint-disable-next-line jam3/no-sanitizer-with-danger
                  dangerouslySetInnerHTML={{
                    __html: t.bankAccount?.bank.iconSvg ?? "",
                  }}
                />

                {/* 3. Handle Bank Name and Last 4 */}
                <span>{t.bankAccount?.last4}</span>

                {/* 4. Close parenthesis if nickname exists */}
                {t.bankAccount?.nickname && <span>)</span>}
              </div>
            </div>
          </div>
        );

        return {
          id: t.id,
          amount: t.amount ?? 0,
          date: t.postingDate,
          label: label,
        };
      }),
    [currencyFormatter, data.txns, stakeholderName]
  );

  return (
    <SharedHistogram
      items={items}
      ratio={ratio}
      title="Your 2025 bank transactions (source of truth)"
      colorClass="bg-blue-500" // Distinct color for txns
    />
  );
}
